import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Typography,
  Tooltip
} from 'antd';
import {
  HeartOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FilterOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { faCalendar, faHourglass, faFileLines, faUser } from '@fortawesome/free-regular-svg-icons';
import { useStore } from '../../hooks/storeContext';
import { ROUTES } from '../../constants/routes';
// AdminLayout now provided at router level via AdminLayoutWrapper
import DashboardService from '../../services/DashboardService';
import BookingService from '../../services/BookingService';
import AnimatedCounter from '../../components/shared/AnimatedCounter';
import MiniSparkline from '../../components/shared/MiniSparkline';
import StatCardSkeleton from '../../components/shared/StatCardSkeleton';
import { usePageHeader } from '../../layouts/AdminLayout';
import {
  PageContainer,
  WelcomeSection,
  StatisticCard,
  StyledTitle,
  WelcomeText,
  EKGPulse,
  StyledStatisticIcon,
  QuickActionTile,
  QuickActionIcon,
  QuickActionTitle,
  QuickActionsGrid,
  DateFilterSection,
  DateFilterLabel,
  DateFilterButtons,
  DateFilterButton,
  SecondaryStatsGrid,
  SecondaryStatCard,
  SecondaryStatIcon,
  SecondaryStatContent,
  RiskCard,
  RiskBar,
  RiskLabel,
  RiskProgress,
  RiskCount,
  EventsCard,
  ViewAllLink,
  EventsList,
  EventItem,
  EventDateBadge,
  EventDateMonth,
  EventDateDay,
  EventInfo,
  EventName,
  EventLocation,
  EventStatus,
  EmptyEvents,
  EmptyEventsText
} from './styled';

const { Text } = Typography;

// Sparkline colors for each stat card
const SPARKLINE_COLORS = {
  heart: '#E31B23',
  team: '#4CAF50',
  file: '#FF9800',
  calendar: '#1890FF'
};

// Date filter options
const DATE_FILTERS = [
  { key: '7days', label: '7 Days' },
  { key: '14days', label: '14 Days' },
  { key: '30days', label: '30 Days' },
  { key: '90days', label: '90 Days' }
];

// Format booking status for display
const formatStatus = (status) => {
  const statusLabels = {
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
    'no-show': 'No Show',
    archived: 'Archived',
    pending: 'Pending'
  };
  return statusLabels[status] || status || 'Pending';
};

const AdminDashboard = observer(() => {
  const [stats, setStats] = useState({
    completedVisits: 0,
    screeningIntake: 0,
    pendingLPATests: 0,
    events: 0
  });
  const [secondaryStats, setSecondaryStats] = useState({
    highRisk: 0,
    pendingReview: 0,
    inQueue: 0,
    followUpRequired: 0
  });
  const [riskDistribution, setRiskDistribution] = useState({
    low: 0,
    moderate: 0,
    high: 0,
    critical: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('7days');
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [showContent, setShowContent] = useState({
    welcome: false,
    stats: false,
    actions: false,
    events: false
  });
  const navigate = useNavigate();
  const { authStore } = useStore();
  const { setPageHeader } = usePageHeader();

  // Set page header
  useEffect(() => {
    setPageHeader({ title: 'Dashboard' });
  }, [setPageHeader]);

  useEffect(() => {
    loadDashboardStats();
    loadUpcomingEvents();
    triggerAnimations();
  }, [dateFilter]);

  const triggerAnimations = () => {
    setTimeout(() => setShowContent(prev => ({ ...prev, welcome: true })), 100);
    setTimeout(() => setShowContent(prev => ({ ...prev, stats: true })), 300);
    setTimeout(() => setShowContent(prev => ({ ...prev, actions: true })), 500);
    setTimeout(() => setShowContent(prev => ({ ...prev, events: true })), 700);
  };

  const loadDashboardStats = async () => {
    setIsLoading(true);
    try {
      // Check if user has dashboard permission before loading stats
      if (!authStore.hasPermission('dashboard')) {
        // Set minimal fallback data for users without dashboard permission
        setStats({
          completedVisits: 0,
          screeningIntake: 0,
          pendingLPATests: 0,
          events: 0
        });
        setIsLoading(false);
        return;
      }

      // Fetch regular stats, booking stats, LPA test count, and patient analytics
      const [statsResponse, bookingStatsResponse, lpaTestStats, patientAnalytics] = await Promise.all([
        DashboardService.getQuickStats(dateFilter),
        BookingService.getBookingStats(),
        DashboardService.getLPATestStats(dateFilter),
        DashboardService.getPatientAnalytics(dateFilter)
      ]);

      // Get the booking count from booking stats
      let bookingCount = 0;
      if (bookingStatsResponse.success && bookingStatsResponse.data) {
        // Use confirmed bookings count (upcoming confirmed appointments)
        bookingCount = bookingStatsResponse.data.data?.confirmedBookings || 
                       bookingStatsResponse.data.confirmedBookings || 
                       bookingStatsResponse.data.data?.activeBookings ||
                       bookingStatsResponse.data.activeBookings || 0;
      }
      
      // Get the total LPA test count filtered by date range
      const totalLPATests = lpaTestStats.total || 0;

      // Use patient analytics for screening data instead of regular stats
      const updatedStats = {
        completedVisits: patientAnalytics.completedVisits || 0,
        screeningIntake: patientAnalytics.screeningIntake || statsResponse.screeningIntake,
        pendingLPATests: totalLPATests,
        events: bookingCount
      };

      setStats(updatedStats);
      
      // Set secondary stats from patient analytics
      setSecondaryStats({
        highRisk: patientAnalytics.highRisk || 0,
        pendingReview: patientAnalytics.pendingReview || 0,
        inQueue: patientAnalytics.pending || 0,
        followUpRequired: patientAnalytics.needsAttention || 0
      });
      
      // Set risk distribution from patient analytics
      if (patientAnalytics.byRisk) {
        setRiskDistribution({
          low: patientAnalytics.byRisk.low || patientAnalytics.byRisk.Low || 0,
          moderate: patientAnalytics.byRisk.moderate || patientAnalytics.byRisk.Moderate || patientAnalytics.byRisk.medium || 0,
          high: patientAnalytics.byRisk.high || patientAnalytics.byRisk.High || 0,
          critical: patientAnalytics.byRisk.critical || patientAnalytics.byRisk.Critical || patientAnalytics.byRisk.very_high || 0
        });
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setIsLoading(false);
      // Intentionally NOT setting mock data so fresh environments show real zeros.
      // Leave existing stats (initially zeros) to accurately reflect no records.
    }
  };
  
  const loadUpcomingEvents = async () => {
    try {
      // Check if user has event_management permission before loading events
      if (!authStore.hasPermission('event_management')) {
        setUpcomingEvents([]);
        return;
      }

      // Fetch upcoming confirmed bookings starting today, sorted soonest first.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingsResponse = await BookingService.getAllBookings({
        page: 1,
        limit: 25,
        status: 'confirmed',
        startDate: today.toISOString(),
        sortField: 'bookingDate',
        sortOrder: 'asc'
      });

      let upcomingBookings = [];

      if (bookingsResponse.success && bookingsResponse.data) {
        const bookingsData = bookingsResponse.data.bookings || bookingsResponse.data;
        
        if (Array.isArray(bookingsData)) {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          
          // Only show events whose START date is today or later.
          // Prefer the authoritative bookingDate because timeSlot.startTime
          // is sometimes stale (not updated on reschedule).
          upcomingBookings = bookingsData
            .filter(booking => {
              const eventStart = new Date(
                booking.bookingDate || booking.timeSlot?.startTime || booking.startDate
              );
              return eventStart >= now;
            })
            .map(booking => {
              // Build location from customData address fields
              const customData = booking.userInfo?.customData || {};
              const addressParts = [];
              if (customData.address1) addressParts.push(customData.address1);
              if (customData.city) addressParts.push(customData.city);
              if (customData.state) addressParts.push(customData.state);
              if (customData.zipCode) addressParts.push(customData.zipCode);
              const formattedLocation = addressParts.length > 0 
                ? addressParts.join(', ') 
                : 'Location TBD';

              return {
                _id: booking._id,
                title: booking.userInfo?.businessEventName || 
                       customData?.businessEventName ||
                       `${booking.eventType?.name || 'Booking'} - ${booking.userInfo?.firstName || ''} ${booking.userInfo?.lastName || ''}`.trim(),
                startDate: booking.bookingDate || booking.timeSlot?.startTime,
                location: {
                  name: formattedLocation
                },
                status: booking.status,
                eventType: {
                  name: booking.eventType?.name || 'Appointment',
                  color: booking.eventType?.color || '#1890ff'
                }
              };
            })
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
            .slice(0, 5);
        }
      }
        
      setUpcomingEvents(upcomingBookings);
    } catch (error) {
      console.error('Error loading upcoming bookings:', error);
    }
  };

  // Handle date filter change
  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
  };
  
  // Calculate total for risk distribution percentages
  const getTotalRisk = () => {
    return riskDistribution.low + riskDistribution.moderate + riskDistribution.high + riskDistribution.critical;
  };
  
  return (
    <>
      {/* Constrained width main content */}
      <PageContainer>

        {/* Welcome Section */}
        <WelcomeSection className={showContent.welcome ? 'animate-in' : 'animate-out'}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <div>
              <StyledTitle level={2}>
                Welcome back, {authStore.me?.fullName?.split(' ')[0] || 'Admin'}!
              </StyledTitle>
              <WelcomeText>
                Here&apos;s your dashboard overview.
              </WelcomeText>
            </div>
            <EKGPulse aria-hidden="true">
              <svg viewBox="0 0 200 60" preserveAspectRatio="none">
                <line x1="0" y1="30" x2="200" y2="30" className="ekg-baseline" />
                {/* Single PQRST waveform built from quadratic curves so every
                    corner is rounded. pathLength normalises stroke math to 1000. */}
                <path
                  className="ekg-track"
                  pathLength="1000"
                  d="M0 30
                     L50 30
                     Q58 30 62 26
                     Q66 22 70 30
                     Q74 38 78 34
                     Q82 30 90 30
                     L108 30
                     Q113 30 115 22
                     Q118 12 121 30
                     Q124 48 127 38
                     Q130 28 134 30
                     L150 30
                     L200 30"
                />
              </svg>
            </EKGPulse>
          </div>
        </WelcomeSection>

        {/* Upcoming Bookings */}
        <div style={{ marginTop: '32px' }} className={showContent.events ? 'actions-animate-in' : 'actions-animate-out'}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Typography.Title level={3} style={{ marginBottom: 0, fontWeight: 'normal' }}>
              Upcoming Bookings
              <Tooltip title="Confirmed upcoming appointments and health screening events. Click on any booking to view details.">
                <InfoCircleOutlined style={{ marginLeft: '8px', fontSize: '16px', color: '#8c8c8c', cursor: 'help' }} />
              </Tooltip>
            </Typography.Title>
            <ViewAllLink href="/admin/events?tab=bookings">
              View All →
            </ViewAllLink>
          </div>
          <EventsCard>
            
            {upcomingEvents.length > 0 ? (
              <EventsList>
                {upcomingEvents.slice(0, 5).map((event, index) => {
                  const eventDate = new Date(event.date || event.startDate);
                  const month = eventDate.toLocaleDateString('en-US', { month: 'short' });
                  const day = eventDate.getDate();
                  const eventTypeColor = event.eventType?.color || '#1890ff';
                  const locationName = event.location 
                    ? (typeof event.location === 'string' ? event.location : event.location.name)
                    : 'Location TBD';
                  
                  return (
                    <EventItem 
                      key={event._id} 
                      $color={eventTypeColor}
                      style={{ animationDelay: `${index * 50}ms` }}
                      onClick={() => navigate(ROUTES.ADMIN_BOOKINGS)}
                    >
                      <EventDateBadge>
                        <EventDateMonth>{month}</EventDateMonth>
                        <EventDateDay>{day}</EventDateDay>
                      </EventDateBadge>
                      
                      <EventInfo>
                        <EventName>{event.title}</EventName>
                        <EventLocation>
                          <EnvironmentOutlined />
                          {locationName}
                        </EventLocation>
                      </EventInfo>
                      
                      <EventStatus $status={event.status}>
                        {formatStatus(event.status)}
                      </EventStatus>
                    </EventItem>
                  );
                })}
              </EventsList>
            ) : (
              <EmptyEvents>
                <CalendarOutlined />
                <EmptyEventsText>No upcoming bookings scheduled</EmptyEventsText>
              </EmptyEvents>
            )}
          </EventsCard>
        </div>

        {/* Date Filter Section */}
        <DateFilterSection style={{ marginTop: '32px' }} className={showContent.stats ? 'stats-animate-in' : 'stats-animate-out'}>
          <DateFilterLabel>
            <FilterOutlined />
            Filter by time period
          </DateFilterLabel>
          <DateFilterButtons>
            {DATE_FILTERS.map((filter) => (
              <DateFilterButton
                key={filter.key}
                $active={dateFilter === filter.key}
                onClick={() => handleDateFilterChange(filter.key)}
              >
                {filter.label}
              </DateFilterButton>
            ))}
          </DateFilterButtons>
        </DateFilterSection>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} className={showContent.stats ? 'stats-animate-in' : 'stats-animate-out'}>
          {isLoading ? (
            <>
              <Col xs={12} sm={12} lg={6}><StatCardSkeleton /></Col>
              <Col xs={12} sm={12} lg={6}><StatCardSkeleton /></Col>
              <Col xs={12} sm={12} lg={6}><StatCardSkeleton /></Col>
              <Col xs={12} sm={12} lg={6}><StatCardSkeleton /></Col>
            </>
          ) : (
            <>
              <Col xs={12} sm={12} lg={6}>
                <StatisticCard style={{ animationDelay: '0ms' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <StyledStatisticIcon className="heart-icon">
                      <HeartOutlined />
                    </StyledStatisticIcon>
                    <div className="stat-value">
                      <AnimatedCounter value={stats.completedVisits} duration={1200} />
                    </div>
                  </div>
                  <Text className="stat-label">
                    Completed Visits
                  </Text>
                  <MiniSparkline color={SPARKLINE_COLORS.heart} height={36} showTrend={true} actualValue={stats.completedVisits} />
                </StatisticCard>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <StatisticCard style={{ animationDelay: '100ms' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <StyledStatisticIcon className="team-icon">
                      <TeamOutlined />
                    </StyledStatisticIcon>
                    <div className="stat-value">
                      <AnimatedCounter value={stats.screeningIntake} duration={1200} />
                    </div>
                  </div>
                  <Text className="stat-label">
                    Bus Screening Intake
                  </Text>
                  <MiniSparkline color={SPARKLINE_COLORS.team} height={36} showTrend={true} actualValue={stats.screeningIntake} />
                </StatisticCard>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <StatisticCard style={{ animationDelay: '200ms' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <StyledStatisticIcon className="file-icon">
                      <FileTextOutlined />
                    </StyledStatisticIcon>
                    <div className="stat-value">
                      <AnimatedCounter value={stats.pendingLPATests} duration={1200} />
                    </div>
                  </div>
                  <Text className="stat-label">
                    Total LPA Tests
                  </Text>
                  <MiniSparkline color={SPARKLINE_COLORS.file} height={36} showTrend={true} actualValue={stats.pendingLPATests} />
                </StatisticCard>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <StatisticCard 
                  style={{ animationDelay: '300ms', cursor: 'pointer' }}
                  onClick={() => navigate(ROUTES.ADMIN_EVENTS)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <StyledStatisticIcon className="calendar-icon">
                      <CalendarOutlined />
                    </StyledStatisticIcon>
                    <div className="stat-value">
                      <AnimatedCounter value={stats.events} duration={1200} />
                    </div>
                  </div>
                  <Text className="stat-label">
                    Bookings
                  </Text>
                  <MiniSparkline color={SPARKLINE_COLORS.calendar} height={36} showTrend={true} actualValue={stats.events} />
                </StatisticCard>
              </Col>
            </>
          )}
        </Row>

        {/* Secondary Stats Row */}
        <div style={{ marginTop: '32px' }} className={showContent.actions ? 'actions-animate-in' : 'actions-animate-out'}>
          <Typography.Title level={3} style={{ marginBottom: '16px', fontWeight: 'normal' }}>
            Member Status Overview
            <Tooltip title="Overview of member statuses including high risk cases, queue status, pending reviews, and follow-up requirements.">
              <InfoCircleOutlined style={{ marginLeft: '8px', fontSize: '16px', color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
          </Typography.Title>
          <SecondaryStatsGrid>
          <SecondaryStatCard>
            <SecondaryStatIcon className="danger">
              <WarningOutlined />
            </SecondaryStatIcon>
            <SecondaryStatContent>
              <div className="stat-value">
                <AnimatedCounter value={secondaryStats.highRisk} duration={1000} />
              </div>
              <div className="stat-label">High Risk Patients</div>
            </SecondaryStatContent>
          </SecondaryStatCard>
          
          <SecondaryStatCard>
            <SecondaryStatIcon className="info">
              <ClockCircleOutlined />
            </SecondaryStatIcon>
            <SecondaryStatContent>
              <div className="stat-value">
                <AnimatedCounter value={secondaryStats.inQueue} duration={1000} />
              </div>
              <div className="stat-label">In Queue</div>
            </SecondaryStatContent>
          </SecondaryStatCard>
          
          <SecondaryStatCard>
            <SecondaryStatIcon className="warning">
              <ExclamationCircleOutlined />
            </SecondaryStatIcon>
            <SecondaryStatContent>
              <div className="stat-value">
                <AnimatedCounter value={secondaryStats.pendingReview} duration={1000} />
              </div>
              <div className="stat-label">Pending Review</div>
            </SecondaryStatContent>
          </SecondaryStatCard>
          
          <SecondaryStatCard>
            <SecondaryStatIcon className="success">
              <CheckCircleOutlined />
            </SecondaryStatIcon>
            <SecondaryStatContent>
              <div className="stat-value">
                <AnimatedCounter value={secondaryStats.followUpRequired} duration={1000} />
              </div>
              <div className="stat-label">Need Follow-up</div>
            </SecondaryStatContent>
          </SecondaryStatCard>
        </SecondaryStatsGrid>
        </div>

        {/* Risk Distribution */}
        {(riskDistribution.low > 0 || riskDistribution.moderate > 0 || riskDistribution.high > 0 || riskDistribution.critical > 0) && (
          <div style={{ marginTop: '32px' }} className={showContent.events ? 'actions-animate-in' : 'actions-animate-out'}>
            <Typography.Title level={3} style={{ marginBottom: '16px', fontWeight: 'normal' }}>
              Member Risk Distribution
              <Tooltip title="Distribution of members by cardiovascular risk level based on health screening results. Risk levels are calculated from blood pressure, cholesterol, and other health indicators.">
                <InfoCircleOutlined style={{ marginLeft: '8px', fontSize: '16px', color: '#8c8c8c', cursor: 'help' }} />
              </Tooltip>
            </Typography.Title>
            <RiskCard>
              <RiskBar>
                <RiskLabel>Low Risk</RiskLabel>
                <RiskProgress 
                  $percent={getTotalRisk() > 0 ? (riskDistribution.low / getTotalRisk()) * 100 : 0} 
                  $color="#52c41a" 
                />
                <RiskCount>{riskDistribution.low}</RiskCount>
              </RiskBar>
              <RiskBar>
                <RiskLabel>Moderate</RiskLabel>
                <RiskProgress 
                  $percent={getTotalRisk() > 0 ? (riskDistribution.moderate / getTotalRisk()) * 100 : 0} 
                  $color="#faad14" 
                />
                <RiskCount>{riskDistribution.moderate}</RiskCount>
              </RiskBar>
              <RiskBar>
                <RiskLabel>High Risk</RiskLabel>
                <RiskProgress 
                  $percent={getTotalRisk() > 0 ? (riskDistribution.high / getTotalRisk()) * 100 : 0} 
                  $color="#ff7a45" 
                />
                <RiskCount>{riskDistribution.high}</RiskCount>
              </RiskBar>
              <RiskBar>
                <RiskLabel>Critical</RiskLabel>
                <RiskProgress 
                  $percent={getTotalRisk() > 0 ? (riskDistribution.critical / getTotalRisk()) * 100 : 0} 
                  $color="#ff4d4f" 
                />
                <RiskCount>{riskDistribution.critical}</RiskCount>
              </RiskBar>
            </RiskCard>
          </div>
        )}

        {/* Quick Actions */}
        <div style={{ marginTop: '32px' }} className={showContent.actions ? 'actions-animate-in' : 'actions-animate-out'}>
          <Typography.Title level={3} style={{ marginBottom: '16px', fontWeight: 'normal' }}>
            Quick Actions
          </Typography.Title>
          <QuickActionsGrid>
              <QuickActionTile onClick={() => navigate(ROUTES.ADMIN_EVENTS)} role="button" tabIndex={0}>
                <QuickActionIcon>
                  <FontAwesomeIcon icon={faCalendar} />
                </QuickActionIcon>
                <QuickActionTitle>Manage Events</QuickActionTitle>
              </QuickActionTile>
            
              <QuickActionTile onClick={() => navigate(ROUTES.ADMIN_ALL_MEMBERS)} role="button" tabIndex={0}>
                <QuickActionIcon>
                  <FontAwesomeIcon icon={faFileLines} />
                </QuickActionIcon>
                <QuickActionTitle>Health Screenings</QuickActionTitle>
              </QuickActionTile>
            
              <QuickActionTile onClick={() => navigate(ROUTES.ADMIN_PATIENT_QUEUE)} role="button" tabIndex={0}>
                <QuickActionIcon>
                  <FontAwesomeIcon icon={faHourglass} />
                </QuickActionIcon>
                <QuickActionTitle>Patient Queue</QuickActionTitle>
              </QuickActionTile>
            
              <QuickActionTile onClick={() => navigate(ROUTES.ADMIN_USERS)} role="button" tabIndex={0}>
                <QuickActionIcon>
                  <FontAwesomeIcon icon={faUser} />
                </QuickActionIcon>
                <QuickActionTitle>Manage Users</QuickActionTitle>
              </QuickActionTile>
            
              <QuickActionTile onClick={() => navigate(ROUTES.ADMIN_ALL_MEMBERS)} role="button" tabIndex={0}>
                <QuickActionIcon>
                  <FontAwesomeIcon icon={faFileLines} />
                </QuickActionIcon>
                <QuickActionTitle>Member records</QuickActionTitle>
              </QuickActionTile>
            
              <QuickActionTile onClick={() => navigate(ROUTES.ADMIN_ANALYTICS)} role="button" tabIndex={0}>
                <QuickActionIcon>
                  <FontAwesomeIcon icon={faChartLine} />
                </QuickActionIcon>
                <QuickActionTitle>Analytics</QuickActionTitle>
              </QuickActionTile>
          </QuickActionsGrid>
        </div>
      </PageContainer>
      </>
  );
});

export default AdminDashboard;
